import React from 'react';
import PropTypes from 'prop-types';
import {parse, stringify} from 'query-string';
import {withRouter} from 'common/utils/with-router';

class StoryboardAutoplayContainer extends React.Component {
  static propTypes = {
    children: PropTypes.any,
    data: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
  };

  constructor() {
    super();
    this.state = {
      isAutoplaying: false,
    };
    this.handleTimer = this.handleTimer.bind(this);
    this.nextChapter = this.nextChapter.bind(this);
    this.restartTimer = this.restartTimer.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.setInitialChapter(
      this.props.data.chapters,
      this.props.navigate,
      this.props.location,
    );
    if (this.props.data.autoplay && !this.props.data.isEditable) {
      this.setState({
        // Autoplay duration is configured in seconds.
        // JS timers need milliseconds:
        autoplayStart: this.props.data.autoplayStart * 1000,
      });
    }
  }

  componentDidMount() {
    if (this.props.data.autoplay && !this.props.data.isEditable) {
      this.addUserInteractionListeners();
      this.restartTimer();
    }
  }

  componentWillUnmount() {
    this.removeUserInteractionListeners();
    clearInterval(this.timer);
  }

  setInitialChapter(chapters, navigate, location) {
    // Ensure that params on the URL correspond to
    // an extant chapter, otherwise remove. This is
    // to prevent the user browsing to content not
    // configured for the current storyboard.
    const {chapterId} = parse(location.search);
    const queryMatchesChapter = chapters.filter(
      (chapter) => chapter.id === parseInt(chapterId, 10),
    ).length;
    if (queryMatchesChapter) {
      // No need to do anything.
      return;
    }

    // Query doesn't correspond to an existing chapter.
    // Attempt to create params based on the first extant chapter.
    if (chapters.length) {
      const chapter = chapters.reduce((previous, current) =>
        previous.position < current.position ? previous : current,
      );
      if (chapter && chapter.id) {
        setTimeout(function () {
          navigate({
            pathname: location.pathname,
            search: stringify({
              chapterId: chapter.id,
            }),
          });
        });
        return;
      }
    }

    /* No query match, no chapters, no nothing. Clear params. */
    setTimeout(function () {
      navigate({
        pathname: location.pathname,
        search: stringify({}),
      });
    });
  }

  addUserInteractionListeners() {
    document.addEventListener('mousemove', this.restartTimer);
    document.addEventListener('touchmove', this.restartTimer);
  }

  nextChapter(location, chapters) {
    const {chapterId} = parse(location.search);
    const currentChapter = chapters.find(
      (chapter) => chapter.id === parseInt(chapterId, 10),
    );

    // Confirm lowest position overall, in case it's not 0
    const lowestPosition = Math.min(
      ...chapters.map((chapter) => chapter.position),
    );

    // Get all chapter positions higher than the current position
    const positions = chapters
      .map((chapter) => chapter.position)
      .filter((position) => position > currentChapter.position);

    // Get next position, defaulting to lowest overall if there is no next
    const nextPosition = positions.length
      ? positions.reduce((acc, current) => (current < acc ? current : acc))
      : lowestPosition;

    const nextChapter = chapters.find(
      (chapter) => chapter.position === nextPosition,
    );

    this.props.navigate({
      pathname: location.pathname,
      search: stringify({
        chapterId: nextChapter.id,
      }),
    });
  }

  removeUserInteractionListeners() {
    document.removeEventListener('mousemove', this.restartTimer);
    document.removeEventListener('touchmove', this.restartTimer);
  }

  restartTimer() {
    console.log('Storyboard: Restart timer');
    if (this.state.isAutoplaying) {
      this.setState({
        isAutoplaying: false,
      });
    }

    clearInterval(this.timer);
    this.timer = setInterval(this.handleTimer, this.state.autoplayStart);
  }

  render() {
    return React.Children.map(this.props.children, (child) => {
      return React.cloneElement(child, {
        isAutoplaying: this.state.isAutoplaying,
        nextChapter: this.nextChapter,
      });
    }).shift();
  }

  handleTimer() {
    console.log('Storyboard: handle timer');
    clearInterval(this.timer);
    this.setState({
      isAutoplaying: true,
    });
  }
}

export default withRouter(StoryboardAutoplayContainer);
